import type { ThemeConfig } from "@/types/theme";

/**
 * Utility functions for theme management with the new classes-based system
 */

// Helper to get theme classes for components
export const getThemeClasses = (theme: ThemeConfig) => theme.classes || {};

// Ensure theme has required properties with sensible defaults for the classes-based system
export const ensureCompleteTheme = (
  theme: Partial<ThemeConfig>
): ThemeConfig => {
  // Default classes that provide fallback styling if no palette is selected
  const defaultClasses: ThemeConfig["classes"] = {
    // Basic colors
    primary: "text-blue-600",
    secondary: "text-blue-700",
    accent: "text-blue-500",

    // Backgrounds
    backgroundPrimary: "bg-white dark:bg-gray-900",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-800",
    backgroundSurface: "bg-white dark:bg-gray-800",
    backgroundCard: "bg-white dark:bg-gray-800",

    // Text colors
    textPrimary: "text-gray-900 dark:text-white",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",

    // Borders
    borderDefault: "border-gray-200 dark:border-gray-700",
    borderLight: "border-gray-100 dark:border-gray-800",

    // Interactive states
    hoverBackground: "hover:bg-gray-50 dark:hover:bg-gray-800",

    // Transitions
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Shadows
    shadowDefault: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",

    // Border radius
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedFull: "rounded-full",

    // Status colors
    error: "text-red-600",
    success: "text-green-600",
    warning: "text-yellow-600",
    info: "text-blue-600",

    errorBg: "bg-red-50 dark:bg-red-900/20",
    successBg: "bg-green-50 dark:bg-green-900/20",
    warningBg: "bg-yellow-50 dark:bg-yellow-900/20",
    infoBg: "bg-blue-50 dark:bg-blue-900/20",
  };

  // Merge provided theme with defaults
  const completeTheme: ThemeConfig = {
    paletteName: theme.paletteName,
    darkMode: theme.darkMode || false,
    classes: {
      ...defaultClasses,
      ...theme.classes,
    },
  };

  return completeTheme;
};

// Get palette-aware complete theme (preferred method)
// Since palettes are now pre-loaded into theme.classes, this just ensures defaults
export const getCompleteThemeWithPalette = (
  theme: ThemeConfig
): ThemeConfig => {
  // Just ensure theme has all required properties with defaults as fallback
  return ensureCompleteTheme(theme);
};

// Memoization cache for theme class lookups
const themeClassCache = new Map<string, string>();

// Helper to safely get a theme class with fallback
export const getThemeClass = (
  theme: ThemeConfig,
  property: keyof NonNullable<ThemeConfig["classes"]>,
  fallback: string = ""
): string => {
  // Create efficient cache key (avoid expensive JSON.stringify)
  const themeId = theme.paletteName || "default-theme";
  const cacheKey = `${themeId}-${property}-${fallback}`;

  // Check cache first
  if (themeClassCache.has(cacheKey)) {
    return themeClassCache.get(cacheKey)!;
  }

  const result = theme.classes?.[property] || fallback;

  // Store in cache
  themeClassCache.set(cacheKey, result);

  // Clear cache if it gets too large (memory management)
  if (themeClassCache.size > 1000) {
    themeClassCache.clear();
  }

  return result;
};

// Check if theme uses palette system
export const usesPaletteSystem = (theme: ThemeConfig): boolean => {
  return Boolean(theme.paletteName || theme.classes);
};

export default {
  getThemeClasses,
  ensureCompleteTheme,
  getCompleteThemeWithPalette,
  getThemeClass,
  usesPaletteSystem,
};
